import confetti from "canvas-confetti";
import { GoalClass } from "@beyondrealityapp/core/goal/classes";
import { GoalStatus } from "@beyondrealityapp/core/goal/constants";
import { Theme } from "@mui/material/styles";

const DAY_MS = 24 * 60 * 60 * 1000;

export type GoalAchievedSummary = {
  goalId: string;
  title: string;
  startedAt: string | null;
  days: number | null;
  subGoalsDone: number;
  subGoalsTotal: number;
  themes: string[];
  // Other goals completed in the same save (a weekly check-in)
  alsoCompleted?: number;
};

// A sub-goal gets a small burst on its card; a top-level goal gets the
// Goal achieved dialog.
export const isTopLevelGoal = (goal: { parent?: string | null }): boolean =>
  !goal.parent;

// What the Goal achieved dialog shows, computed when the goal is completed.
export const goalAchievedSummary = (
  goal: GoalClass,
  now: Date = new Date()
): GoalAchievedSummary => {
  const start =
    goal.startDate instanceof Date
      ? goal.startDate
      : goal.metaData?.createdAt instanceof Date
      ? goal.metaData.createdAt
      : null;
  const days =
    start && start.getTime() <= now.getTime()
      ? Math.floor((now.getTime() - start.getTime()) / DAY_MS)
      : null;
  const children = Object.values(goal.children ?? {});
  return {
    goalId: goal.id ?? "",
    title: goal.content ?? "",
    startedAt: days === null || !start ? null : start.toISOString(),
    days,
    subGoalsDone: children.filter(
      (child) => child.status === GoalStatus.COMPLETED
    ).length,
    subGoalsTotal: children.length,
    themes: goal.themes ?? [],
  };
};

// Confetti in the brand colours.
export const completionColors = (theme: Theme): string[] => [
  theme.palette.primary.main,
  theme.palette.secondary.main,
  theme.palette.primary.light,
  theme.palette.warning.main,
  theme.palette.purple.main,
];

// Viewport-relative centre of an element, as canvas-confetti's `origin`.
// Falls back to the bottom centre when there is no element to start from.
export const confettiOrigin = (
  element: Element | null | undefined,
  viewport: { width: number; height: number } = {
    width: window.innerWidth,
    height: window.innerHeight,
  }
): { x: number; y: number } => {
  if (!element || !viewport.width || !viewport.height) {
    return { x: 0.5, y: 0.8 };
  }
  const rect = element.getBoundingClientRect();
  return {
    x: (rect.left + rect.width / 2) / viewport.width,
    y: (rect.top + rect.height / 2) / viewport.height,
  };
};

// Small burst from the element that completed the goal (a card's checkbox).
export const burstFrom = (
  element: Element | null | undefined,
  colors: string[]
) =>
  confetti({
    particleCount: 40,
    spread: 70,
    startVelocity: 22,
    gravity: 1.1,
    ticks: 120,
    scalar: 0.8,
    origin: confettiOrigin(element),
    colors,
    disableForReducedMotion: true,
  });

// Confetti falling from the top edge over the Goal achieved dialog.
export const confettiRain = (colors: string[]) =>
  confetti({
    particleCount: 120,
    angle: 270,
    spread: 160,
    startVelocity: 12,
    gravity: 0.6,
    drift: 0,
    ticks: 320,
    origin: { x: 0.5, y: -0.1 },
    colors,
    zIndex: 1400,
    disableForReducedMotion: true,
  });
